/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module LogsModule
 */

import { ElementRef } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Observable } from 'rxjs';
import { LogsSearchBarDropdownService } from './logs-search-bar-dropdown.service';
import { ILogsGroupbyResponseData, ILogsGroupbyResponseResultData } from '../logs.types';
import { ILogsStates, LogsStore } from './logs.store';
import { DropdownOptionType } from '../components/traffic-logs/traffic-logs-search-bar/traffic-logs-search-bar.store';

/**
 * @param fullString - full string that is currently being edited in the input.
 * @param property - filter property, e.g. 'method'.
 * @param operator - filter operator, e.g. '='.
 * @param groupbyValues - groupby data for the current filter property.
 */
interface IInputFilterProps {
    fullString: string;
    property: string;
    operator: string;
    groupbyValues: ILogsGroupbyResponseResultData[];
}

/**
 * @param inputFilter
 *  Several filters can exist in the search bar but only one is edited at a time.
 *  These are the props of the current filter being edited in the input.
 * @param isLoading - flag to indicate a request for the search bar is in progress.
 * @param isFilterBeingEdited - flag to indicate whether a filter is being edited.
 * @param hasError - whether there is an error loading groupby values.
 */
export interface ILogsSearchBarStates {
    inputFilter: IInputFilterProps
    isLoading: boolean;
    isFilterBeingEdited: boolean;
    hasError: boolean;
}

/**
 * @description
 *      The LogsStore is the source of truth for filters.
 *      The search bar component reads filter state from the LogsStore
 *      and updates it according to search bar interaction.
 *
 *      This store manages state for any filter being typed or edited by the user
 *      and the dropdown.
 *
 * @author Yogesh Patil
 */
export abstract class LogsSearchBarStore <T extends ILogsSearchBarStates> extends ComponentStore<T> {
    /**
     * Reference to the primary filter input.
     */
    public primaryFilterInput: ElementRef<HTMLInputElement>;

    // ********************************** Selectors **********************************

    public readonly inputFilterValue$ = this.select(state => state.inputFilter.fullString);

    public readonly inputFilterProperty$ = this.select(state => state.inputFilter.property);

    public readonly inputFilterOperator$ = this.select(state => state.inputFilter.operator);

    public readonly groupbyValues$ = this.select(state => state.inputFilter.groupbyValues);

    public readonly isLoading$ = this.select(state => state.isLoading);

    public readonly isFilterBeingEdited$ = this.select(state => state.isFilterBeingEdited);

    public readonly hasError$ = this.select(state => state.hasError);

    public abstract readonly dropdownOptionType$: Observable<DropdownOptionType>;

    // ********************************** Updaters **********************************

    public readonly setInputFilterValue = this.updater((state, inputFilterValue: string) => ({
        ...state,
        inputFilter: {
            ...state.inputFilter,
            fullString: inputFilterValue,
        },
    }));

    public readonly setInputFilterProperty = this.updater((state, property: string) => ({
        ...state,
        inputFilter: {
            ...state.inputFilter,
            property,
        },
    }));

    public readonly setInputFilterOperator = this.updater((state, operator: string) => ({
        ...state,
        inputFilter: {
            ...state.inputFilter,
            operator,
        },
    }));

    public readonly setIsFilterBeingEdited = this.updater(
        (state, isFilterBeingEdited: boolean) => ({
            ...state,
            isFilterBeingEdited,
        }),
    );

    protected readonly setGroupbyValues = this.updater(
        (state, groupbyValues: ILogsGroupbyResponseResultData[]) => ({
            ...state,
            inputFilter: {
                ...state.inputFilter,
                groupbyValues,
            },
        }),
    );

    protected readonly setIsLoading = this.updater((state, isLoading: boolean) => ({
        ...state,
        isLoading,
    }));

    protected readonly setHasError = this.updater((state, hasError: boolean) => ({
        ...state,
        hasError,
    }));

    protected initialState: T;

    // ********************************** Constructor **********************************

    constructor(
        initialState: T,
        protected readonly logsStore: LogsStore<ILogsStates>,
        protected readonly logsSearchBarDropdownService: LogsSearchBarDropdownService,
    ) {
        super(initialState);
        this.initialState = initialState;
    }

    // ********************************** Methods **********************************

    public resetInputFilterProperty(): void {
        this.setInputFilterProperty('');
    }

    public resetInputFilterOperator(): void {
        this.setInputFilterOperator('');
    }

    public resetGroupbyValues(): void {
        this.setGroupbyValues([]);
    }

    /**
     * Focus the primary filter input.
     */
    public focusPrimaryInput(): void {
        this.primaryFilterInput.nativeElement.focus();
    }

    /**
     * Set isLoading to true upon API request.
     */
    protected startLoading = (): void => {
        this.setHasError(false);
        this.setIsLoading(true);
    };

    /**
     * Set the groupby data to state upon successful API request.
     */
    protected handleApiData = (data: ILogsGroupbyResponseData): void => {
        this.setGroupbyValues(data.results);
    };

    /**
     * Set the error state and an empty list if an error occurs during groupby request.
     */
    protected handleApiError = (): void => {
        this.setGroupbyValues([]);
        this.setHasError(true);
        this.setIsLoading(false);
    };

    /**
     * Set isLoading to false upon API request complete.
     */
    protected handleApiComplete = (): void => {
        this.setIsLoading(false);
    };

    // ****************************** Abstract Methods ******************************
    /**
     * Close the dropdown and reset the search bar state.
     */
    protected abstract resetSearchBar(): void;

    /**
     * Set params for filters that are being edited.
     */
    public abstract setEdit(index: number): void;

    public abstract incrementDropdownStage(): void;

    public abstract decrementDropdownStage(): void;

    public abstract requestGroupbyValues(): void;

    public abstract addFilter(): void;

    public abstract editFilter(index: number): void;
}
